// Account: the signed-in person's API keys and connected apps (docs/plans/claude-connector.md
// §5). Every role may hold them; a key acts as its owner and never beyond the owner's role.
//
// Reads `GET /me/credentials` (`myCredentials`, warmed by the route). Creating a key shows
// its secret once, in the sheet, and nowhere else: not the query cache, not the URL, not
// browser storage, not a log. Closing the sheet forgets it.

import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { CredentialList } from '@/components/credential-list';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import { Icons } from '@/components/primitives';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { useCreateApiKey, useRevokeMyCredential } from '@/api/credential-mutations';
import { API_BASE } from '@/api/base';
import { myCredentials } from '@/api/resources';
import { FRESHNESS } from '@/api/resource';
import { useRefreshWhenOlderThan } from '@/lib/use-refresh-when-older-than';
import { useMe } from '@/auth/useMe';
import { fmtDate } from '@/lib/datetime';
import {
  ACCESS_BLURB,
  ACCESS_LABEL,
  issuableScopes,
  API_KEY_LIFETIMES_DAYS,
  API_KEY_NAME_MAX,
  DEFAULT_API_KEY_LIFETIME_DAYS,
  claudeCodeCommand,
  connectorUrl,
  curlExample,
  type ApiKeyLifetimeDays,
} from '@/lib/credentials';
import { RoleBadge } from './admin-shared';
import { ConnectHelp } from './account-connect-help';
import type { CredentialScope, CredentialSummary } from '@/api/types';

const SECTION_LABEL = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.14em',
  color: 'var(--ink-3)',
  textTransform: 'uppercase',
  marginBottom: 6,
} as const;

/** A notice that an admin has switched something off. */
function SwitchedOff({ children }: { children: string }) {
  return (
    <div
      role="note"
      style={{
        padding: '10px 12px',
        marginBottom: 12,
        background: 'var(--bg-sunk)',
        border: '1px solid var(--line)',
        borderLeft: '3px solid var(--warn)',
        borderRadius: 4,
        fontSize: 12,
        color: 'var(--ink-1)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function AccountView() {
  const { data: me } = useMe();
  const query = useQuery(myCredentials(undefined));
  // "Last used" moves without a revision; never show it more than a minute old on arrival.
  useRefreshWhenOlderThan(query, FRESHNESS.minute);
  const revoke = useRevokeMyCredential();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<{ id: string; error: unknown } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const data = query.data;
  const keys = data?.credentials.filter((c) => c.kind === 'api_key') ?? [];
  const apps = data?.credentials.filter((c) => c.kind === 'oauth_grant') ?? [];

  const onRevoke = (credential: CredentialSummary) => {
    setRevokingId(credential.id);
    setRevokeError(null);
    setNotice(null);
    revoke.mutate(credential.id, {
      onSuccess: () => setNotice(`Revoked ${credential.kind === 'oauth_grant'
        ? (credential.client_name ?? credential.name)
        : `the key ${credential.name}`}.`),
      onError: (error) => setRevokeError({ id: credential.id, error }),
      onSettled: () => setRevokingId(null),
    });
  };

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Account</h1>
        </div>
      </div>

      <div role="region" aria-label="Account" tabIndex={0} style={{ flex: 1, overflow: 'auto', padding: '22px 22px 60px' }}>
        <div style={{ maxWidth: 820 }}>
          {me && (
            <div className="panel" style={{ padding: '12px 14px', marginBottom: 24, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px' }}>
              <span style={{ fontWeight: 650 }}>{me.name}</span>
              <span className="mono muted" style={{ fontSize: 11.5, overflowWrap: 'anywhere' }}>{me.email}</span>
              <RoleBadge role={me.role} />
            </div>
          )}

          <div role="status" style={{ marginBottom: notice ? 14 : 0 }}>
            {notice && (
              <div style={{
                padding: '10px 12px',
                background: 'color-mix(in oklch, var(--good) 12%, transparent)',
                border: '1px solid color-mix(in oklch, var(--good) 40%, var(--line))',
                borderLeft: '3px solid var(--good)',
                borderRadius: 4,
                display: 'flex', alignItems: 'center', gap: 10,
                fontSize: 12, color: 'var(--ink-1)',
              }}>
                <span style={{ color: 'var(--good)' }}>{Icons.check}</span>
                <span>{notice}</span>
              </div>
            )}
          </div>

          {query.isError && (
            <QueryErrorNote error={query.error} what="your API keys" onRetry={() => { void query.refetch(); }} />
          )}
          {query.isPending && (
            <div className="muted" style={{ padding: '24px 0', fontSize: 13, fontStyle: 'italic' }}>Loading your API keys…</div>
          )}

          {data && (
            <>
              <section aria-labelledby="account-api-keys" style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <h2 id="account-api-keys" tabIndex={-1} style={SECTION_LABEL}>API keys</h2>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--ink-1)' }}>
                      A key lets a script or Claude Code work with your account without signing in.
                      It acts as you, and can never do more than your role allows: if your role
                      changes, so does what the key can do. No key can launch, pause or change
                      anything that is live.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!data.api_keys_enabled}
                    title={data.api_keys_enabled ? undefined : 'An admin has switched API keys off'}
                    onClick={() => { setNotice(null); setCreateOpen(true); }}
                  >
                    {Icons.plus} Create API key
                  </button>
                </div>
                {!data.api_keys_enabled && (
                  <SwitchedOff>
                    An admin has switched API keys off. No key can be created, and the keys below
                    are refused until they are switched back on. None of them has been revoked.
                  </SwitchedOff>
                )}
                <CredentialList
                  credentials={keys}
                  label="Your API keys"
                  focusFallbackId="account-api-keys"
                  viewer="owner"
                  empty="You have no API keys."
                  onRevoke={onRevoke}
                  revokingId={revokingId}
                  revokeError={revokeError}
                />
              </section>

              <section aria-labelledby="account-connected-apps">
                <h2 id="account-connected-apps" tabIndex={-1} style={SECTION_LABEL}>Connected apps</h2>
                <p style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.55, color: 'var(--ink-1)' }}>
                  Apps you signed in to with this account, such as Claude or ChatGPT. Each acts as you and
                  never beyond your role. Revoke one to cut it off at once.
                </p>
                {!data.mcp_enabled && (
                  <SwitchedOff>
                    An admin has switched connected apps off. They are refused until they are
                    switched back on. None of them has been revoked.
                  </SwitchedOff>
                )}
                {data.mcp_enabled && (
                  <ConnectHelp
                    connector={connectorUrl(API_BASE, window.location.origin)}
                    apiKeysEnabled={data.api_keys_enabled}
                    defaultOpen={apps.length === 0}
                  />
                )}
                <CredentialList
                  credentials={apps}
                  label="Your connected apps"
                  focusFallbackId="account-connected-apps"
                  viewer="owner"
                  empty={data.mcp_enabled
                    ? 'No apps are connected yet.'
                    : 'No apps are connected. You connect Claude or ChatGPT from the app itself, once an admin turns connected apps on.'}
                  onRevoke={onRevoke}
                  revokingId={revokingId}
                  revokeError={revokeError}
                />
              </section>
            </>
          )}
        </div>
      </div>

      {createOpen && data && (
        <CreateApiKeySheet
          maxScope={data.max_scope}
          mcpEnabled={data.mcp_enabled}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}

type CopyState = 'idle' | 'copied' | 'selected';

interface CreateApiKeySheetProps {
  /** The most the caller's role lets a key do. */
  maxScope: CredentialScope;
  /** Connected apps on: a key also reaches the connector, so the sheet shows the Claude Code line. */
  mcpEnabled: boolean;
  onClose: () => void;
}

/**
 * The form, then the new key's secret, once. The secret lives in this component's state only:
 * read from the create's own result, cleared synchronously on close, and the create mutation
 * reset with it (`useCreateApiKey` keeps it for no one). A result that lands after the sheet
 * closed is dropped.
 */
function CreateApiKeySheet({ maxScope, mcpEnabled, onClose }: CreateApiKeySheetProps) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<CredentialScope>('read');
  const [days, setDays] = useState<ApiKeyLifetimeDays>(DEFAULT_API_KEY_LIFETIME_DAYS);
  const [secret, setSecret] = useState<string | null>(null);
  const [created, setCreated] = useState<CredentialSummary | null>(null);
  const [copy, setCopy] = useState<CopyState>('idle');
  const secretField = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const secretId = useId();
  const create = useCreateApiKey();
  const required = useRequiredFeedback();
  const mounted = useRef(false);
  const resetCreate = useRef(create.reset);
  resetCreate.current = create.reset;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      resetCreate.current();
    };
  }, []);

  const close = () => {
    setSecret(null);
    setCreated(null);
    create.reset();
    onClose();
  };

  const trimmed = name.trim();
  const nameIssue = !trimmed
    ? 'Name the key, so you can tell it apart from others later.'
    : trimmed.length > API_KEY_NAME_MAX
      ? `Keep the name to ${API_KEY_NAME_MAX} characters.`
      : null;

  const submit = () => {
    if (create.isPending) return;
    if (!required.report([nameIssue && { key: 'api-key-name', message: nameIssue }])) return;
    create.mutateAsync({ name: trimmed, scope, expires_in_days: days }).then(
      (response) => {
        // Closed while the request was out: the key exists and is listed, but its secret is
        // not shown to a sheet nobody is looking at.
        if (!mounted.current) return;
        setSecret(response.secret);
        setCreated(response.credential);
      },
      // The failure is on `create.error`, shown in the form.
      () => undefined,
    );
  };

  const copySecret = () => {
    if (!secret) return;
    const select = () => {
      secretField.current?.focus();
      secretField.current?.select();
      setCopy('selected');
    };
    if (!navigator.clipboard?.writeText) {
      select();
      return;
    }
    navigator.clipboard.writeText(secret).then(() => setCopy('copied'), select);
  };

  const expiresOn = fmtDate(Date.now() + days * 86_400_000);
  const curl = curlExample(API_BASE, window.location.origin);

  return (
    <ResponsiveOverlay label="Create API key" kind="form" onClose={close} className="modal-host">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">{secret ? 'Copy your new key' : 'Create API key'}</div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
              {secret
                ? 'This is the only time it is shown. If you lose it, revoke it and create another.'
                : 'The key acts as you, and never beyond your role.'}
            </div>
          </div>
          <button className="modal-x" aria-label="Close" onClick={close}>×</button>
        </div>

        {secret ? (
          <div className="modal-body">
            <label className="field-lbl" htmlFor={secretId}>
              {created ? `Key “${created.name}”` : 'Your new key'}
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input
                id={secretId}
                ref={secretField}
                className="input mono"
                readOnly
                value={secret}
                autoComplete="off"
                spellCheck={false}
                onFocus={(e) => e.currentTarget.select()}
                style={{ flex: '1 1 0', minWidth: 0, fontSize: 12 }}
              />
              <button type="button" className="btn" onClick={copySecret}>
                {Icons.copy} {copy === 'copied' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div role="status" className="muted" style={{ fontSize: 11.5, minHeight: 18 }}>
              {copy === 'copied' && 'Copied. Paste it somewhere safe now.'}
              {copy === 'selected' && 'Selected. Press Ctrl+C (⌘C on a Mac) to copy it.'}
            </div>
            <div className="field-lbl" style={{ marginTop: 12 }}>Try it</div>
            <code
              className="mono"
              style={{
                display: 'block', fontSize: 11.5, padding: '8px 10px', background: 'var(--bg-sunk)',
                border: '1px solid var(--line)', borderRadius: 4, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap',
              }}
            >
              {curl}
            </code>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>
              Put your key in place of &lt;key&gt;. It answers with who you are.
              {created && ` The key works until ${fmtDate(created.expires_at)}, unless you revoke it.`}
            </div>
            {mcpEnabled && (
              <>
                <div className="field-lbl" style={{ marginTop: 12 }}>Use it in Claude Code</div>
                <code
                  className="mono"
                  style={{
                    display: 'block', fontSize: 11.5, padding: '8px 10px', background: 'var(--bg-sunk)',
                    border: '1px solid var(--line)', borderRadius: 4, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap',
                  }}
                >
                  {claudeCodeCommand(connectorUrl(API_BASE, window.location.origin))}
                </code>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>
                  Again with your key in place of &lt;key&gt;. Claude Code keeps it in its settings file.
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="modal-body">
            <RequiredField
              fieldKey="api-key-name"
              message={required.messages['api-key-name']}
              attempt={required.attempt}
              className="field"
              style={{ marginBottom: 14 }}
            >
              <label className="field-lbl" htmlFor={nameId}>Name</label>
              <input
                id={nameId}
                className="input"
                autoFocus
                maxLength={API_KEY_NAME_MAX + 20}
                placeholder="Weekly sales export"
                value={name}
                onChange={(e) => {
                  const next = e.target.value;
                  setName(next);
                  create.reset();
                  const nextTrimmed = next.trim();
                  if (nextTrimmed && nextTrimmed.length <= API_KEY_NAME_MAX) required.clear('api-key-name');
                }}
              />
            </RequiredField>

            <div className="field" style={{ marginBottom: 14 }}>
              <div className="field-lbl" id={`${nameId}-access`}>Access</div>
              {maxScope !== 'read' ? (
                <div role="group" aria-labelledby={`${nameId}-access`} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  {issuableScopes(maxScope).map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={scope === option}
                      onClick={() => setScope(option)}
                      style={{
                        flex: '1 1 200px', textAlign: 'left', cursor: 'pointer', padding: '10px 12px',
                        border: '1px solid ' + (scope === option ? 'var(--accent)' : 'var(--line)'),
                        borderRadius: 'var(--r)',
                        background: scope === option ? 'color-mix(in oklch, var(--accent) 8%, var(--panel))' : 'var(--panel)',
                        color: 'var(--ink)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 650, fontSize: 12.5, marginBottom: 4 }}>
                        {ACCESS_LABEL[option]}
                        {scope === option && <span style={{ color: 'var(--accent)' }}>{Icons.check}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.4 }}>{ACCESS_BLURB[option]}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  {ACCESS_LABEL.read}. {ACCESS_BLURB.read} Your role can read only, so its keys can too.
                </div>
              )}
            </div>

            <div className="field" style={{ marginBottom: 4 }}>
              <div className="field-lbl" id={`${nameId}-expiry`}>Expires after</div>
              <div role="group" aria-labelledby={`${nameId}-expiry`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {API_KEY_LIFETIMES_DAYS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="btn btn-sm"
                    aria-pressed={days === option}
                    onClick={() => setDays(option)}
                    style={days === option
                      ? { borderColor: 'var(--accent)', background: 'color-mix(in oklch, var(--accent) 10%, var(--panel))' }
                      : undefined}
                  >
                    {option} days
                  </button>
                ))}
              </div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>It stops working on {expiresOn}.</div>
            </div>

            {create.error ? (
              <div style={{ marginTop: 10 }}>
                <MutationErrorLine error={create.error} />
              </div>
            ) : null}
          </div>
        )}

        <div className="modal-foot">
          {secret ? (
            <button type="button" className="btn btn-primary" onClick={close}>Done</button>
          ) : (
            <>
              <button type="button" className="btn btn-ghost" onClick={close}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={create.isPending} onClick={submit}>
                {Icons.key} {create.isPending ? 'Creating…' : 'Create key'}
              </button>
            </>
          )}
        </div>
      </div>
    </ResponsiveOverlay>
  );
}

export { AccountView, CreateApiKeySheet };
